import React, { memo, ReactNode } from "react";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { Image } from "@UI/Components/Image/Image";
import { Text } from "@UI/Components/Text/Text";

type TMetricIconStatusState = "default" | "success" | "error" | "pending";

const iconColorByState: Record<TMetricIconStatusState, string> = {
  default: "icon",
  success: "seaGreen.600",
  error: "red.600",
  pending: "orange.500",
};

const iconBgByState: Record<TMetricIconStatusState, string> = {
  default: "gray.300",
  success: "green.50",
  error: "red.50",
  pending: "orange.50",
};

interface IMetricIconStatus {
  message: ReactNode;
  icon?: React.ElementType;
  imageUrl?: string;
  state?: TMetricIconStatusState;
  iconBg?: string;
}

export const MetricIconStatus = memo(({
  message,
  icon,
  imageUrl,
  state = "default",
  iconBg,
}: IMetricIconStatus) => (
  <Flex align="center" gap={2}>
    <Flex
      position="relative"
      align="center"
      justify="center"
      w="44px"
      h="44px"
      rounded="full"
      bg={iconBg ?? iconBgByState[state]}
      flexShrink={0}
      overflow="hidden"
      border={imageUrl && state === "pending" ? "2px" : undefined}
      borderStyle={imageUrl && state === "pending" ? "dashed" : undefined}
      borderColor={imageUrl && state === "pending" ? "gray.700" : undefined}
    >
      {imageUrl ? (
        <Image
          src={imageUrl}
          alt=""
          w="full"
          h="full"
          objectFit="cover"
          filter={state === "pending" || state === "error" ? "saturate(0)" : undefined}
        />
      ) : icon ? (
        <Icon as={icon} color={iconColorByState[state]} boxSize="24px" />
      ) : null}
      {imageUrl && (state === "pending" || state === "error") && (
        <Box
          position="absolute"
          inset={0}
          bg={state === "pending" ? "orange.500" : "red.500"}
          opacity={0.3}
        />
      )}
    </Flex>
    <Text fontSize="sm" color="gray.600">
      {message}
    </Text>
  </Flex>
));

MetricIconStatus.displayName = "MetricIconStatus";
